/**
 * Helpers de formatação compartilhados.
 */

import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

export function formatTemperature(v: unknown, fractionDigits = 1): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return `${n.toFixed(fractionDigits)} °C`;
}

export function formatPower(v: unknown, fractionDigits = 0): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return `${n.toFixed(fractionDigits)} W`;
}

export function formatNumber(v: unknown, fractionDigits = 1): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return n.toFixed(fractionDigits);
}

export function formatPercent(v: unknown, fractionDigits = 0): string {
  const n = toNumber(v);
  if (n === null) return "—";
  return `${n.toFixed(fractionDigits)}%`;
}

/**
 * Duração legível (PT-BR) a partir de segundos.
 *  - < 60s:        "12s"
 *  - < 1h:         "3min" / "12min 45s"
 *  - < 1d:         "2h 14min"
 *  - >= 1d:        "3d 4h"
 *
 * `showSeconds` controla se o componente de segundos aparece quando a
 * duração é maior que 1 minuto (default false, mais limpo).
 */
export function formatDuration(seconds: number, showSeconds = false): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  if (showSeconds && sec > 0) return `${m}min ${sec}s`;
  return `${m}min`;
}

/**
 * Converte unknown -> number ou null.
 *
 * Aceita number, string parseable, boolean (0/1) e strings booleanas
 * ("true"/"false"/"on"/"off"/"open"/"closed") — caso típico do Zigbee
 * door sensor que publica `contact: true|false` (boolean) mas que pode
 * chegar como string serializada vinda do `payload->>field` do Postgres.
 */
export function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const lower = v.toLowerCase().trim();
    if (lower === "true"  || lower === "on"  || lower === "open")   return 1;
    if (lower === "false" || lower === "off" || lower === "closed") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** "há 3 minutos", "há 2 horas", etc. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return "—";
  }
}

/** Data legível BR. */
export function formatDate(iso: string | null | undefined, pattern = "dd/MM/yyyy HH:mm"): string {
  if (!iso) return "—";
  try {
    return format(new Date(iso), pattern, { locale: ptBR });
  } catch {
    return "—";
  }
}

/** Trunca UUID pra exibição. */
export function shortId(id: string | null | undefined, len = 8): string {
  if (!id) return "—";
  return id.slice(0, len);
}

/** Mapping role-do-device → label legível em PT. */
export const ROLE_LABEL: Record<string, string> = {
  temp_internal:     "Temp. interna",
  temp_external:     "Temp. externa",
  temp_product:      "Temp. produto",
  humidity_internal: "Umidade interna",
  humidity_external: "Umidade externa",
  door:              "Porta",
  power:             "Tomada",
  composite:         "All-in-one",
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return ROLE_LABEL[role] ?? role;
}
